import type { Theme } from '@emotion/react';
import { css, keyframes } from '@emotion/react';

import { breakpoint, duration } from '@twistopayments/design-system/styles';

// Caret blink cadence — a text-cursor rhythm, not a DS transition token.
const caretBlinkDuration = 950;

export const pin = (theme: Theme) => css`
  margin: 0 ${theme.spacing[8]};
  padding: ${theme.spacing[8]};
  font-size: ${theme.typography.app.h1Mobile.fontSize}px;
  line-height: ${theme.typography.app.h1Mobile.lineHeight}px;
  letter-spacing: ${theme.typography.app.h1Mobile.letterSpacing}px;
  font-weight: ${theme.typography.fontWeight.medium};
  transition: background-color ${duration.short}ms, color ${duration.short}ms;
  border-radius: ${theme.borderRadius.circle};
  background: ${theme.palette.tertiary.lowerMidContrast};
  box-shadow: inset 0 0 0 1px ${theme.palette.tertiary.lowContrast};

  ${breakpoint.up('sm')} {
    margin: 0 ${theme.spacing[12]};
    font-size: ${theme.typography.app.h1.fontSize}px;
    line-height: ${theme.typography.app.h1.lineHeight}px;
    letter-spacing: ${theme.typography.app.h1.letterSpacing}px;
  }

  &:after {
    display: block;
    height: ${theme.spacing[8]};
    width: ${theme.spacing[8]};
    content: '';
    border-radius: ${theme.borderRadius.circle};
    transition: background-color ${duration.short}ms;
    /* Same colour as the cell — the dot only reads on the current/filled cell */
    background: ${theme.palette.tertiary.lowerMidContrast};
  }
  ${theme.presentation.pin.frame}
`;

export const filledClass = (theme: Theme) => css`
  background: ${theme.presentation.selection.filled};
  box-shadow: none;

  &:after {
    background: ${theme.presentation.selection.strong};
  }
`;

export const charPinClass = (theme: Theme) => css`
  background: none;
  box-shadow: none;
  border-radius: 0;
  height: auto;
  width: ${theme.spacing[24]};
  padding: 0;
  color: ${theme.palette.text.title};

  &:after {
    content: none;
  }
  ${theme.presentation.pin.character}
`;

export const readOnlyClass = (theme: Theme) => css`
  color: ${theme.palette.text.title};
`;

const blinkAnimation = keyframes`
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
`;

export const currentClass = (theme: Theme) => css`
  &:after {
    background: ${theme.palette.tertiary.upperMidContrast};
  }

  label:focus-within &:${theme.presentation.pin.caret} {
    animation: ${blinkAnimation} ${caretBlinkDuration}ms infinite ease;
  }
  ${theme.presentation.pin.current}
`;
